/**
 * Subskrypcja powiadomień po stronie przeglądarki.
 *
 * Tylko Android — na tym się umówiliśmy i to upraszcza sprawę: nie ma obejść
 * pod iOS, gdzie push wymaga zainstalowanej PWA i zachowuje się inaczej.
 */

export interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
  subscribed: boolean;
}

/** Czy przeglądarka w ogóle to potrafi. */
export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function fetchPushConfig(): Promise<PushConfig> {
  const response = await fetch("/api/push/config", { credentials: "same-origin" });
  if (!response.ok) return { enabled: false, publicKey: null, subscribed: false };
  return (await response.json()) as PushConfig;
}

/**
 * Klucz VAPID jedzie jako base64url, a `PushManager` chce surowych bajtów.
 * Zamiana jest mechaniczna, ale bez niej subskrypcja kończy się
 * `InvalidCharacterError`, co niczego nie tłumaczy.
 */
function base64UrlToBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Bufor tworzony jawnie: `Uint8Array.from` daje `ArrayBufferLike`, którego
  // `PushManager` nie przyjmuje — mógłby to być SharedArrayBuffer.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Włącza powiadomienia. Zwraca `false`, gdy użytkownik odmówił.
 *
 * O zgodę pytamy dopiero po kliknięciu przełącznika, nigdy przy starcie —
 * systemowe okienko pokazane od razu po wejściu jest odruchowo odrzucane
 * i wtedy nie da się już o nią poprosić ponownie.
 */
export async function enablePush(publicKey: string): Promise<boolean> {
  if (!pushSupported()) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Bez tego Chrome odmawia subskrypcji: powiadomienie musi być widoczne
      // dla użytkownika, a nie cichym kanałem do aplikacji.
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    }));

  const response = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(subscription.toJSON()),
  });
  return response.ok;
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await fetch("/api/push/unsubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  }).catch(() => undefined);

  await subscription.unsubscribe();
}
